import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { BangumiProviderService } from '../../bangumi-provider.service';
import { finalize, Subscription } from 'rxjs';
import { ServiceClient, WebhookHealth } from '../../../sdk/bangumiProvider';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { UIToast, UIToastComponent, UIToastRef } from '@irohalab/deneb-ui';
import { NgClass } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { TurnstileComponent } from '../../ui/turnstile/turnstile.component';
import { CopyOnClickDirective } from '../../ui/copy-on-click.directive';

@Component({
    selector: 'app-service-client',
    templateUrl: './service-client.component.html',
    imports: [
    FormsModule,
    ReactiveFormsModule,
    NgClass,
    TurnstileComponent,
    CopyOnClickDirective
],
    styleUrl: './service-client.component.less'
})
export class ServiceClientComponent implements OnInit, OnDestroy {
  private subscription = new Subscription();
  private toastRef: UIToastRef<UIToastComponent>;
  protected readonly isClearingPendingEvents = signal(false);

  @Input()
  clientId!: string;

  client!: ServiceClient;
  clientFormGroup!: FormGroup;
  deleteFormGroup!: FormGroup;
  readonly deleteConfirmDigitsCount: number = 6;
  secretVisibility: string = 'password';
  turnstileToken: string | null = null;
  mode: 'edit' | 'register' = 'edit';

  get pendingWebhookEvents(): string {
    if (this.client?.pendingWebhookEvents) {
      return JSON.stringify(this.client.pendingWebhookEvents, null, 2);
    }
    return '';
  }

  constructor(private bangumiProviderService: BangumiProviderService,
              private route: ActivatedRoute,
              private router: Router,
              toast: UIToast,
              private formBuilder: FormBuilder) {
    this.clientFormGroup = formBuilder.group({
      webhookUrl: new FormControl('', Validators.required),
    });
    this.toastRef = toast.makeText();
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  ngOnInit(): void {
    this.route.params.subscribe(params => {
      this.clientId = params['id'];
    })
    this.subscription.add(
      this.bangumiProviderService
        .getServiceClient(this.clientId)
        .subscribe({
          next: (client) => {
            this.client = client;
            this.clientFormGroup.patchValue({webhookUrl: this.client.webhookUrl});
            this.deleteFormGroup = this.formBuilder.group({
              confirmText: new FormControl('', [
                Validators.required,
                Validators.pattern(new RegExp(`^${this.client.clientId.substring(0, this.deleteConfirmDigitsCount)}`))
              ]),
            });
            this.mode = 'edit';
          }, error: (ex) => {
            if (ex.error && ex.error.code === 'NOT_FOUND') {
              this.mode = 'register';
            } else {
              this.toastRef.show(ex.message ? ex.message : ex);
            }
          }
        })
    );
  }

  updateWebhookUrl(): void {
    if (this.mode !== 'edit' && !this.turnstileToken) {
      return;
    }
    const webhookUrl = this.clientFormGroup.value.webhookUrl;
    if (this.mode === 'edit') {
      this.subscription.add(
        this.bangumiProviderService.updateServiceClient(this.clientId, {webhookUrl})
          .subscribe({
            next: (client) => {
              this.clientFormGroup.markAsPristine();
              this.client = client;
            }, error: (error) => {
              this.toastRef.show(error.message ? error.message : error);
            }
          })
      );
    } else {
      const serviceClient = {clientId: this.clientId, webhookUrl} as ServiceClient;
      this.subscription.add(
        this.bangumiProviderService.registerServiceClient(serviceClient, String(this.turnstileToken))
          .subscribe({
            next: (client) => {
              this.clientFormGroup.markAsPristine();
              this.client = client;
              this.mode = 'edit';
            }, error: (ex) => {
              if (ex.error && ex.error.message) {
                this.toastRef.show(ex.message);
              } else {
                this.toastRef.show(ex.message ? ex.message : ex);
              }
            }
          })
      );
    }
  }

  rotateSecret(): void {
    this.subscription.add(
      this.bangumiProviderService.updateServiceClient(this.clientId, {secret: 'rotate'})
        .subscribe({
          next: (client) => {
            this.client.secret = client.secret;
            this.toastRef.show('Secret rotate successfully.');
          }, error: (error) => {
            this.toastRef.show(error.message ? error.message : error);
          }
        })
    );
  }

  resetWebhookHealth(): void {
    this.subscription.add(
      this.bangumiProviderService.updateServiceClient(this.clientId, {
        webhookHealth: WebhookHealth.Healthy,
        consecutiveFailureCount: 0
      })
        .subscribe({
          next: (updatedClient: ServiceClient) => {
            this.client = updatedClient;
            this.toastRef.show('Webhook health status reset!');
          }, error: (error) => {
            this.toastRef.show(error.message ? error.message : error);
          }
        })
    );
  }

  clearPendingWebhookEvents(): void {
    if (!window.confirm('Clear all pending webhook events? These events will not be delivered.')) {
      return;
    }
    this.isClearingPendingEvents.set(true);
    this.subscription.add(
      this.bangumiProviderService.clearPendingWebhookEvents(this.clientId)
        .pipe(finalize(() => this.isClearingPendingEvents.set(false)))
        .subscribe({
          next: (updatedClient: ServiceClient) => {
            this.client = updatedClient;
            this.toastRef.show('Pending webhook events cleared.');
          }, error: (error) => {
            this.toastRef.show(error.message ? error.message : error);
          }
        })
    );
  }

  toggleSecretVisibility(): void {
    if (this.secretVisibility === 'password') {
      this.secretVisibility = 'text';
    } else {
      this.secretVisibility = 'password';
    }
  }

  onTurnstileTokenVerified(token: string|null): void {
    this.turnstileToken = token;
  }

  onTurnstileTokenError(error: string|null): void {
    this.toastRef.show(error);
  }

  onTurnstileTokenExpired(): void {
    this.turnstileToken = null;
  }

  backToOAuth2ClientConfig(): void {
    this.router.navigate(['settings', 'developer', 'client', this.clientId], {queryParams: {mode: 'edit'}}).then(() => {});
  }

  deleteWebhook(): void {
    this.subscription.add(
      this.bangumiProviderService.deleteServiceClient(this.clientId)
        .subscribe({
          next: () => {
            this.toastRef.show('Delete service client successfully.');
            this.backToOAuth2ClientConfig();
          },
          error: (error) => {
            this.toastRef.show(error.message ? error.message : error);
          }
        })
    );
  }
}
